/**
 * Unified setup form hook.
 * Key difference from the old useCampaignSetupForm: always auto-adds a name-derived tag.
 * This file is a standalone copy so the old flow stays untouched and can be removed later.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Campaign, CampaignSubscriber, CampaignType } from '@/shared/types/campaigns';
import { convertNameToTag } from '@/util/util';
import { TAG_LENGTH } from '@/util/validation/constants';

import {
  updateDraftCampaign,
  addCustomTag,
  setDraftAudienceStats,
  setDraftAutonomousActions,
  setDraftOutreachGoal,
  setDraftPaymentRecoveryGoal,
  setDraftPaymentRecoveryGuidelines,
} from '../../../redux/actions';
import {
  selectCsvLiveInstructionTargets,
  selectDraftSubscribers,
  selectDraftCampaignName,
  selectDraftAiAgentData,
  selectDraftCampaignTags,
  selectCustomTags,
  selectDraftComplianceProfileId,
  selectDraftCampaignFileName,
  selectDraftCampaignType,
  selectDraftAudienceStats,
  selectDraftPaymentRecoveryGoal,
  selectDraftPaymentRecoveryGuidelines,
  selectDraftOutreachGoal,
  selectDraftAutonomousActions,
  selectSelectedCampaign,
} from '../../../redux/selectors';
import { computeAudienceStats, transformDraftSubscribersToApiFormat } from '../../../utils';
import { createCampaignSetupSchema, GOAL_MAX_LENGTH } from '../../../validator';
import type { CampaignSetupForm } from '../../payment-recovery/types';
import {
  GOAL_TO_NBA_GOAL,
  NBA_GOAL_TO_GOAL,
} from '../../payment-recovery/useBuildDebtCampaignLogic';

export interface UseUnifiedSetupFormOptions {
  campaignId: string;
  campaign: Campaign | undefined;
  apiSubscribers: CampaignSubscriber[] | undefined;
  hasUserDeletedAudience: boolean;
  shouldReplaceSubscribers: boolean;
  updateCampaignAsync: (payload: Partial<Campaign> & { campaign_id: string }) => Promise<Campaign>;
  bulkUpsertSubscribersAsync: (payload: {
    campaign_id: string;
    subscribers: ReturnType<typeof transformDraftSubscribersToApiFormat>;
    replace: boolean;
  }) => Promise<unknown>;
}

export const useUnifiedSetupForm = ({
  campaignId,
  campaign,
  apiSubscribers,
  hasUserDeletedAudience,
  shouldReplaceSubscribers,
  updateCampaignAsync,
  bulkUpsertSubscribersAsync,
}: UseUnifiedSetupFormOptions) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux state
  const subscribers = useSelector(selectDraftSubscribers);
  const draftCampaignName = useSelector(selectDraftCampaignName);
  const draftFileName = useSelector(selectDraftCampaignFileName);
  const draftAiAgentData = useSelector(selectDraftAiAgentData);
  const draftTags = useSelector(selectDraftCampaignTags);
  const customTags = useSelector(selectCustomTags);
  const draftComplianceProfileId = useSelector(selectDraftComplianceProfileId);
  const draftCampaignType = useSelector(selectDraftCampaignType);
  const draftAudienceStats = useSelector(selectDraftAudienceStats);
  const draftPaymentRecoveryGoal = useSelector(selectDraftPaymentRecoveryGoal);
  const draftPaymentRecoveryGuidelines = useSelector(selectDraftPaymentRecoveryGuidelines);
  const draftOutreachGoal = useSelector(selectDraftOutreachGoal);
  const draftAutonomousActions = useSelector(selectDraftAutonomousActions);
  const selectedCampaign = useSelector(selectSelectedCampaign);
  const csvLiveInstructionTargets = useSelector(selectCsvLiveInstructionTargets);

  // Unified-specific derived state
  const campaignType = (draftCampaignType as CampaignType) || 'marketing';
  const autonomousActions = draftAutonomousActions;
  const goal = draftPaymentRecoveryGoal || '';
  const outreachGoal = draftOutreachGoal || '';

  const initializedCampaignIdRef = useRef<string | null>(null);
  const hasSyncedRef = useRef(false);

  // Combine custom tags with campaign name tag and draft tags
  const allTagOptions = useMemo(() => {
    const options = [...customTags];
    const campaignTagValue = convertNameToTag(draftCampaignName);

    if (campaignTagValue) {
      const campaignTag = { label: campaignTagValue, value: campaignTagValue, disabled: true };
      if (!options.some((opt) => opt.value === campaignTag.value)) {
        options.unshift(campaignTag);
      }
    }

    draftTags.forEach((tag) => {
      if (!options.some((opt) => opt.value === tag.value)) {
        options.push(tag);
      }
    });

    return options;
  }, [customTags, draftCampaignName, draftTags]);

  // Always auto-add campaign name tag (unified v2 behaviour)
  const ensuredTags = useMemo(() => {
    const campaignTagValue = convertNameToTag(draftCampaignName);

    if (!campaignTagValue) {
      return draftTags;
    }

    const campaignTag = { label: campaignTagValue, value: campaignTagValue };
    if (draftTags.some((tag) => tag.value === campaignTag.value)) {
      return draftTags;
    }

    return [campaignTag, ...draftTags];
  }, [draftTags, draftCampaignName]);

  // Default name for new campaigns — use the base name as placeholder;
  // the findAvailableName effect will resolve the final name via check_name API.
  const defaultCampaignName = useMemo(() => {
    const baseName = t('campaigns.new_campaign');
    return draftCampaignName || baseName;
  }, [t, draftCampaignName]);

  // Form setup
  const form = useForm<CampaignSetupForm>({
    resolver: yupResolver(
      createCampaignSetupSchema(campaign?.name)
    ) as unknown as Resolver<CampaignSetupForm>,
    defaultValues: {
      name: defaultCampaignName,
      audience: null,
      aiAgent: '',
      fileName: draftFileName || '',
      tags: ensuredTags,
      complianceProfile: draftComplianceProfileId || 'default',
      evaluateFirstMessage: false,
    },
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Sync Effects
  // ─────────────────────────────────────────────────────────────────────────────

  // Reset form when loading a different campaign
  // NOTE: Dependency on campaign_id only (not entire object) prevents reset on draft updates
  useEffect(() => {
    if (!campaign) {
      return;
    }

    const currentId = campaign.campaign_id;
    const previousId = initializedCampaignIdRef.current;

    // Same campaign - don't reset
    if (currentId === previousId) {
      return;
    }

    // For draft campaigns: only initialize once, don't reset on subsequent updates
    // Draft campaigns may have undefined/null campaign_id
    const isDraft = !currentId;
    const wasPreviouslyInitialized = previousId !== null;
    if (isDraft && wasPreviouslyInitialized) {
      return;
    }

    // Preserve audience from Redux if exists (don't wipe uploaded CSV)
    let preservedAudience: File | null = null;
    if (subscribers.length > 0 && draftFileName) {
      preservedAudience = new File([''], draftFileName, { type: 'text/csv' });
    }

    // Use draft name from Redux only if Redux has been initialized for this campaign
    // (i.e., selectedCampaign was set by useInitializeCampaignRedux). Otherwise use
    // the API value to avoid picking up draft state left over from another campaign.
    const isReduxSynced = selectedCampaign?.campaign_id === campaign.campaign_id;
    const resolvedName = isReduxSynced ? draftCampaignName || campaign.name : campaign.name;

    form.reset({
      name: resolvedName,
      audience: preservedAudience,
      aiAgent: campaign.brain_parent_id || '',
      tags: draftTags,
      complianceProfile: campaign.nba_config?.compliance_profile_id || '',
      evaluateFirstMessage: campaign.nba_config?.evaluate_first_message ?? false,
    });

    // Mark as initialized - use 'draft' sentinel for draft campaigns
    initializedCampaignIdRef.current = currentId || 'draft';
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally depend on campaign_id only to prevent reset on draft property changes
  }, [campaign?.campaign_id, form, draftTags, subscribers.length, draftFileName]);

  // Sync autonomous actions and goal from campaign's NBA config on initial load only
  useEffect(() => {
    if (hasSyncedRef.current || !campaign?.nba_config) {
      return;
    }
    hasSyncedRef.current = true;

    if (!autonomousActions) {
      dispatch(setDraftAutonomousActions(true));
    }

    // Sync goal - use campaign.type directly since Redux draft type may not be set yet
    if (campaign.nba_config.goal) {
      const isPaymentRecovery = campaign.type === 'payment_recovery';

      if (isPaymentRecovery) {
        const agentGoal =
          NBA_GOAL_TO_GOAL[campaign.nba_config.goal as keyof typeof NBA_GOAL_TO_GOAL];
        if (agentGoal) {
          dispatch(setDraftPaymentRecoveryGoal(agentGoal));
        }
      } else {
        dispatch(setDraftOutreachGoal(campaign.nba_config.goal));
      }
    }
  }, [campaign, autonomousActions, dispatch]);

  // Sync aiAgent fields with Redux state
  useEffect(() => {
    if (draftAiAgentData?.brain_parent_id) {
      form.setValue('aiAgent', draftAiAgentData.brain_parent_id);
      form.setValue('aiAgentVersion', draftAiAgentData.brain_version);
      form.clearErrors('aiAgent');
    }
  }, [draftAiAgentData?.brain_parent_id, draftAiAgentData?.brain_version, form]);

  // Sync complianceProfile field with Redux state
  useEffect(() => {
    if (draftComplianceProfileId) {
      form.setValue('complianceProfile', draftComplianceProfileId);
    }
  }, [draftComplianceProfileId, form]);

  // Validate audience when subscribers change
  useEffect(() => {
    if (subscribers.length === 0) {
      form.clearErrors('audience');
      return;
    }

    const validSubscribers = subscribers.filter((sub) => !sub.error);

    if (validSubscribers.length === subscribers.length) {
      form.clearErrors('audience');
    } else if (validSubscribers.length === 0) {
      form.setError('audience', {
        type: 'manual',
        message: t('campaigns.setup.no_valid_subscribers'),
      });
    } else {
      form.clearErrors('audience');
    }
  }, [subscribers, form, t]);

  // Clear audience errors when API subscribers are loaded (on page refresh)
  useEffect(() => {
    if ((apiSubscribers?.length ?? 0) > 0 && !hasUserDeletedAudience) {
      form.clearErrors('audience');
    }
  }, [apiSubscribers?.length, hasUserDeletedAudience, form]);

  // Sync ensured tags (with auto-generated campaign name tag) to Redux
  useEffect(() => {
    if (ensuredTags !== draftTags) {
      dispatch(updateDraftCampaign({ tags: ensuredTags }));
      form.setValue('tags', ensuredTags);
    }
  }, [ensuredTags, draftTags, dispatch, form]);

  // Keep form tags synced with Redux draftTags
  useEffect(() => {
    const currentFormTags = form.getValues('tags') || [];
    const currentTagValues = currentFormTags.map((tag) => tag.value).sort();
    const draftTagValues = draftTags.map((tag) => tag.value).sort();

    if (JSON.stringify(currentTagValues) !== JSON.stringify(draftTagValues)) {
      form.setValue('tags', draftTags, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [draftTags, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  /** Update campaign name and auto-generate tag */
  const handleCampaignNameChange = useCallback(
    (value: string) => {
      const oldCampaignTagValue = convertNameToTag(draftCampaignName);
      const newCampaignTagValue = convertNameToTag(value);

      const otherTags = draftTags.filter((tag) => tag.value !== oldCampaignTagValue && tag.value);

      let updatedTags = otherTags;
      if (newCampaignTagValue) {
        const newCampaignTag = { label: newCampaignTagValue, value: newCampaignTagValue };
        updatedTags = [newCampaignTag, ...otherTags];
      }

      dispatch(updateDraftCampaign({ name: value, tags: updatedTags }));
      form.setValue('tags', updatedTags, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });

      return value;
    },
    [dispatch, draftCampaignName, draftTags, form]
  );

  /** Handle AI agent selection */
  const handleBrainSubmit = useCallback(
    (selection: { brain_parent_id: string; brain_version: number; brain_name: string }) => {
      form.setValue('aiAgent', selection.brain_parent_id);
      form.setValue('aiAgentVersion', selection.brain_version);
      form.clearErrors('aiAgent');

      dispatch(
        updateDraftCampaign({
          brain_parent_id: selection.brain_parent_id,
          brain_version: selection.brain_version,
          brain_name: selection.brain_name,
        })
      );
    },
    [dispatch, form]
  );

  /** Handle compliance profile selection */
  const handleComplianceProfileSelect = useCallback(
    (value: string) => {
      form.clearErrors('complianceProfile');
      dispatch(updateDraftCampaign({ compliance_profile_id: value }));
    },
    [dispatch, form]
  );

  /** Handle tags selection with validation */
  const handleTagsChange = useCallback(
    (selectedTags: { label: string; value: string }[]) => {
      const invalidTags = selectedTags.filter((tag) => {
        const tagPattern = /^[\w][\w-]*$/i;
        return !tag.label || tag.label.length > TAG_LENGTH || !tagPattern.test(tag.label);
      });

      if (invalidTags.length > 0) {
        form.setError('tags', { type: 'manual', message: t('dialog.tags.regex_error') });
        return;
      }

      form.clearErrors('tags');
      dispatch(updateDraftCampaign({ tags: selectedTags }));
    },
    [dispatch, form, t]
  );

  /** Create a new custom tag with validation */
  const handleCreateNewTag = useCallback(
    (newTagValue: string) => {
      const trimmedValue = newTagValue.trim();
      const tagPattern = /^[\w][\w-]*$/i;

      if (!trimmedValue) {
        form.setError('tags', { type: 'manual', message: t('validation.required') });
        return;
      }
      if (trimmedValue.length > TAG_LENGTH) {
        form.setError('tags', {
          type: 'manual',
          message: t('validation.less_than', { 0: TAG_LENGTH }),
        });
        return;
      }
      if (!tagPattern.test(trimmedValue)) {
        form.setError('tags', { type: 'manual', message: t('dialog.tags.regex_error') });
        return;
      }

      const newTag = {
        label: trimmedValue,
        value: trimmedValue.toLowerCase().replace(/\s+/g, '-'),
      };

      const tagExists = allTagOptions.some(
        (tag) =>
          tag.value === newTag.value || tag.label.toLowerCase() === newTag.label.toLowerCase()
      );
      if (tagExists) {
        form.setError('tags', { type: 'manual', message: t('validation.name_already_exists') });
        return;
      }

      form.clearErrors('tags');
      dispatch(addCustomTag(newTag));
      dispatch(updateDraftCampaign({ tags: [...draftTags, newTag] }));
    },
    [dispatch, draftTags, form, t, allTagOptions]
  );

  /** Handle autonomous actions toggle */
  const handleAutonomousActionsChange = useCallback(
    (checked: boolean) => {
      dispatch(setDraftAutonomousActions(checked));
      if (!checked) {
        // Clear goal, guidelines, and compliance profile when disabling autonomous actions
        dispatch(setDraftPaymentRecoveryGoal(''));
        dispatch(setDraftOutreachGoal(''));
        dispatch(setDraftPaymentRecoveryGuidelines(null));
        dispatch(updateDraftCampaign({ compliance_profile_id: '' }));
        form.setValue('complianceProfile', '');
        form.setValue('goal', '');
        form.setValue('outreachGoal', '');
        form.setValue('evaluateFirstMessage', false);
      }
    },
    [dispatch, form]
  );

  /** Handle payment recovery goal change */
  const handleGoalChange = useCallback(
    (value: string) => {
      dispatch(setDraftPaymentRecoveryGoal(value));
    },
    [dispatch]
  );

  /** Handle outreach goal change */
  const handleOutreachGoalChange = useCallback(
    (value: string) => {
      dispatch(setDraftOutreachGoal(value));
    },
    [dispatch]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Form Validation & Submission
  // ─────────────────────────────────────────────────────────────────────────────

  /** Validate audience and AI agent fields */
  const validateRequiredFields = useCallback(() => {
    let isValid = true;

    // Check for valid subscribers (without errors) - not just any subscribers.
    // When draft subscribers exist (user uploaded a new file), validate only those —
    // don't fall back to API subscribers since the draft replaces them.
    const validDraftSubscribers = subscribers.filter((sub) => !sub.error);
    const hasValidSubscribers =
      subscribers.length > 0
        ? validDraftSubscribers.length > 0
        : !hasUserDeletedAudience && (apiSubscribers?.length ?? 0) > 0;

    if (!hasValidSubscribers) {
      const resolvedType = draftCampaignType || campaign?.type || 'payment_recovery';
      const isPaymentRecovery = resolvedType === 'payment_recovery';
      const errorMessageKey =
        subscribers.length > 0
          ? 'campaigns.setup.no_valid_subscribers'
          : isPaymentRecovery
            ? 'campaigns.setup.audience_required_payment_recovery'
            : 'campaigns.setup.audience_required';

      form.setError('audience', { type: 'manual', message: t(errorMessageKey) });
      isValid = false;
    }

    return isValid;
  }, [
    subscribers,
    form,
    t,
    hasUserDeletedAudience,
    apiSubscribers,
    draftCampaignType,
    campaign?.type,
  ]);

  /** Save campaign data (used by Continue and Save & Exit) */
  const saveCampaignData = useCallback(
    async (validateAll: boolean = true): Promise<Campaign | null> => {
      const formName = form.getValues().name;
      if (!formName?.trim()) {
        form.setError('name', { type: 'manual', message: t('validation.required') });
        return null;
      }

      if (validateAll && !validateRequiredFields()) {
        return null;
      }

      // Frontend guard: never POST an over-length outreach goal to the backend.
      // Runs on every save path (Continue and Save & Exit) since the backend
      // rejects it regardless. Gate on the same `campaignType` that controls the
      // goal field's visibility so enforcement can't drift from what's editable.
      if (
        draftAutonomousActions &&
        campaignType !== 'payment_recovery' &&
        (draftOutreachGoal ?? '').length > GOAL_MAX_LENGTH
      ) {
        form.setError('outreachGoal', {
          type: 'manual',
          message: t('campaigns.setup.goal_max_length', { 0: GOAL_MAX_LENGTH }),
        });
        return null;
      }

      try {
        const formValues = form.getValues();
        const brainParentId = formValues.aiAgent || draftAiAgentData?.brain_parent_id;
        const brainVersion = formValues.aiAgentVersion ?? draftAiAgentData?.brain_version;
        const complianceProfileId =
          formValues.complianceProfile || draftComplianceProfileId || undefined;
        // Use draft type from Redux, fall back to existing campaign type, or default to payment_recovery
        const resolvedType = draftCampaignType || campaign?.type || 'payment_recovery';

        // Build NBA config for campaigns with autonomous actions enabled
        let nbaConfig: unknown = undefined;
        let isAgentic: boolean | undefined = undefined;

        if (draftAutonomousActions) {
          if (resolvedType === 'payment_recovery' && complianceProfileId) {
            // Payment recovery with autonomous actions: full NBA config
            isAgentic = true;
            nbaConfig = {
              ...campaign?.nba_config,
              ...(draftPaymentRecoveryGoal && {
                goal: GOAL_TO_NBA_GOAL[draftPaymentRecoveryGoal as keyof typeof GOAL_TO_NBA_GOAL],
              }),
              compliance_profile_id: complianceProfileId,
              ...(draftPaymentRecoveryGuidelines && { guidelines: draftPaymentRecoveryGuidelines }),
              evaluate_first_message: formValues.evaluateFirstMessage === true,
            };
          } else if (resolvedType !== 'payment_recovery') {
            // Outreach with autonomous actions: NBA config with goal and guidelines
            // Guidelines are stored in draftPaymentRecoveryGuidelines (shared state from useBuildDebtCampaignLogic)
            const outreachGuidelines = draftPaymentRecoveryGuidelines;
            isAgentic = true;
            nbaConfig = {
              ...campaign?.nba_config,
              ...(draftOutreachGoal && { goal: draftOutreachGoal }),
              ...(outreachGuidelines && { guidelines: outreachGuidelines }),
              evaluate_first_message: formValues.evaluateFirstMessage === true,
            };
          }
        } else if (!draftAutonomousActions && campaign?.nba_config) {
          // Autonomous actions disabled: send is_agentic: false, omit nba_config (forbidden by API)
          isAgentic = false;
        }

        // Persisted stats count only the rows actually saved; variables_count is a column count, keep it.
        const validSubscribers = subscribers.filter((sub) => !sub.error);
        const persistedAudienceStats =
          draftAudienceStats && subscribers.length > 0
            ? computeAudienceStats(validSubscribers, draftAudienceStats.variables_count)
            : draftAudienceStats;

        // Only include brain fields if both brainParentId and brainVersion have actual values
        const campaignPayload = {
          ...(brainParentId &&
            brainVersion != null && {
              brain_parent_id: brainParentId,
              brain_version: brainVersion,
            }),
          subscribers_context: {
            tags: draftTags.map((tag) => tag.value),
            ...(csvLiveInstructionTargets &&
              csvLiveInstructionTargets.length > 0 && {
                live_instructions_keys: csvLiveInstructionTargets,
              }),
          },
          type: resolvedType as Campaign['type'],
          ...(isAgentic !== undefined && { is_agentic: isAgentic }),
          ...(nbaConfig !== undefined && { nba_config: nbaConfig }),
          metadata: { ...(campaign?.metadata || {}), ...persistedAudienceStats },
        } as unknown as Partial<Campaign>;

        const savedCampaign = await updateCampaignAsync({
          campaign_id: campaignId,
          name: formName,
          ...campaignPayload,
        });

        await bulkUpsertSubscribersAsync({
          campaign_id: savedCampaign.campaign_id,
          subscribers: transformDraftSubscribersToApiFormat(validSubscribers),
          replace: shouldReplaceSubscribers,
        });
        // The Redux draft survives SPA nav — sync its stats to what was persisted.
        if (persistedAudienceStats) {
          dispatch(setDraftAudienceStats(persistedAudienceStats));
        }

        return savedCampaign;
      } catch (error) {
        console.error('Error saving campaign:', error);
        return null;
      }
    },
    [
      form,
      validateRequiredFields,
      t,
      draftAiAgentData?.brain_parent_id,
      draftAiAgentData?.brain_version,
      draftComplianceProfileId,
      draftCampaignType,
      campaignType,
      draftPaymentRecoveryGoal,
      draftPaymentRecoveryGuidelines,
      draftOutreachGoal,
      draftAutonomousActions,
      draftAudienceStats,
      campaign?.type,
      campaign?.metadata,
      campaign?.nba_config,
      draftTags,
      campaignId,
      subscribers,
      dispatch,
      bulkUpsertSubscribersAsync,
      updateCampaignAsync,
      shouldReplaceSubscribers,
      csvLiveInstructionTargets,
    ]
  );

  return {
    form,
    draftCampaignName,
    draftAiAgentData,
    draftTags,
    allTagOptions,
    handleCampaignNameChange,
    handleBrainSubmit,
    handleComplianceProfileSelect,
    handleTagsChange,
    handleCreateNewTag,
    saveCampaignData,
    // Unified-specific state
    campaignType,
    autonomousActions,
    goal,
    outreachGoal,
    // Unified-specific handlers
    handleAutonomousActionsChange,
    handleGoalChange,
    handleOutreachGoalChange,
  };
};
